// frontend/src/components/masters/CustomerForm.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './CustomerForm.css';
import { toast } from 'react-toastify';
import { openFilePreview } from '../common/FilePreviewModal';
import EmailInput from '../common/EmailInput';
import { validateFormEmails } from '../common/EmailValidation';
import Select2Dropdown from '../common/Select2Dropdown';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import the permissions hook


const API_BASE_URL = '/api/masters/customers/';
const BANKS_API_URL = '/api/masters/banks/';
const COUNTRIES_API_URL = '/api/masters/countries/';
const GEONAMES_USERNAME = 'salimh.';


// Helper to get auth token
const getAuthHeaders = (isFormData = false) => {
    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
    };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    // For FormData, do not set Content-Type, let the browser do it.
    return headers;
};


const CustomerForm = ({ mode, initialData, onClose }) => {
    // Form state
    const [customer, setCustomer] = useState(initialData || {
        name: '', short_name: '', phone: '', address: 'P.O.BOX ', city: '', country: 'TZ', client_reference_required: false, letterhead: null,
        tin: '', tin_document: null, vrn: '', vrn_document: null, remarks: '',
        contact_persons: [], email_groups: [], credentials: [], tariffs: [], attachments: [],
        bank_details: [{ bank: '', swift_code: '', currency: 'TZS', account_number: '' }], // Set default bank row
        destinations: [], // New state for destinations
        vrn_na: false
    });
    const [readOnly, setReadOnly] = useState(mode === 'view');
    const [error, setError] = useState(null);
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [tanzanianCities, setTanzanianCities] = useState([]); // For destinations
    const [banks, setBanks] = useState([]); // For bank details

    // --- INSERT: get permission checker from context ---
    const { hasPermission } = usePermissions();


    // Add state for email validation errors
    const [emailValidationErrors, setEmailValidationErrors] = useState({});
    const [showCredentialPasswords, setShowCredentialPasswords] = useState([]);
    const [notes, setNotes] = useState([]);


    // Refs for file inputs to manage actual File objects for FormData
    const tinDocumentRef = useRef(null);
    const vrnDocumentRef = useRef(null);
    const letterheadRef = useRef(null);
    
    // Refs for preview modal
    const previewModalRef = useRef(null);
    const previewIframeRef = useRef(null);
    const previewImageRef = useRef(null); 
    const previewDownloadDivRef = useRef(null);
    const [isMaximized, setIsMaximized] = useState(false);

    // Initial data effect
    useEffect(() => {
        if (initialData) {
            console.log('Initial data loaded:', initialData);
            console.log('TIN document:', initialData.tin_document);
            console.log('VRN document:', initialData.vrn_document);
            
            const addressToSet = (!initialData.address || initialData.address === '')
                ? 'P.O.BOX '
                : initialData.address.startsWith('P.O.BOX') 
                    ? initialData.address 
                    : `P.O.BOX ${initialData.address}`;

            setCustomer({
                ...initialData,
                address: addressToSet,
                country: initialData.country || '',
                vrn_na: !!initialData.vrn_na,
                bank_details: initialData.bank_details || [],
                destinations: initialData.destinations || [],
            });
            setNotes((initialData.notes || []).map(n => ({
                ...n,
                subject: (n.subject || '').toUpperCase(),
                notes: (n.notes || '').toUpperCase()
            })));
        } else {
            setCustomer(prev => ({ ...prev, address: 'P.O.BOX ' }));
        }
        setReadOnly(mode === 'view');
    }, [mode, initialData]);

    // Resolve stored country name -> CCA2 once countries list is loaded; normalize city case
    useEffect(() => {
        if (countries.length > 0 && initialData?.country) {
            const found = countries.find(
                c => c.name.toUpperCase() === initialData.country.toUpperCase() || c.cca2 === initialData.country
            );
            if (found) {
                setCustomer(prev => ({
                    ...prev,
                    country: found.cca2,
                    city: prev.city ? prev.city.toUpperCase() : prev.city,
                }));
            }
        }
    }, [countries, initialData]);

    // --- Geolocation and Phone Formatting ---
    const loadCountries = useCallback(async () => {
        try {
            const token = localStorage.getItem('kk_token');
            const response = await fetch(COUNTRIES_API_URL, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setCountries(Array.isArray(data) ? data : (data.results || []));
        } catch (err) {
            console.error('Failed to load countries:', err);
            setError('Failed to load countries.');
        }
    }, []);
        

    const loadBanks = useCallback(async () => {
        try {
            const response = await fetch(BANKS_API_URL, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error('Failed to load banks');
            const data = await response.json();
            setBanks(data);
        } catch (err) {
            toast.error('Failed to load banks.');
        }
    }, []);

    const updateCities = useCallback(async (countryCode, isForDestinations = false) => {
        if (!countryCode || countryCode.length !== 2) return;
        try {
            const response = await fetch(`https://secure.geonames.org/searchJSON?country=${countryCode}&featureClass=P&maxRows=1000&username=${GEONAMES_USERNAME}`);
            const data = await response.json();
            const cityList = data.geonames ? [...new Set(data.geonames.map(city => city.name))].sort() : [];
            if (isForDestinations) setTanzanianCities(cityList);
            else setCities(cityList);
        } catch (err) {
            toast.error(`Failed to load cities for ${countryCode}.`);
        }
    }, []);

    const countryPhoneCodes = {
        "TZ": "+255", "KE": "+254", "UG": "+256", "RW": "+250", "BI": "+257",
    };

    const setPhoneCodeAndFormat = useCallback((countryCode) => {
        const code = countryPhoneCodes[countryCode] || "+";
        setCustomer(prev => ({ ...prev, phone: `${code}-` }));
    }, [countryPhoneCodes]);

    const formatPhoneInput = useCallback((value) => {
        const countryCode = customer.country;
        const code = countryPhoneCodes[countryCode] || "+";
        let digits = value.replace(new RegExp(`^\\${code}-?`), "").replace(/\D/g, "");
        let formatted = code + "-";
        if (digits.length > 0) formatted += digits.slice(0, 3);
        if (digits.length > 3) formatted += "-" + digits.slice(3, 6);
        if (digits.length > 6) formatted += "-" + digits.slice(6, 9);
        return formatted;
    }, [customer.country, countryPhoneCodes]);

    const formatTIN = useCallback((value) => {
        let digits = value.replace(/\D/g, '').slice(0, 9);
        let formatted = '';
        if (digits.length > 0) formatted += digits.slice(0, 3);
        if (digits.length > 3) formatted += '-' + digits.slice(3, 6);
        if (digits.length > 6) formatted += '-' + digits.slice(6, 9);
        return formatted;
    }, []);

    const formatVRN = useCallback((value) => {
      let val = value.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
      let formatted = '';
      if (val.length > 0) formatted = val.slice(0, 2);
      if (val.length > 2) formatted += '-' + val.slice(2, 8);
      if (val.length > 8) formatted += '-' + val.slice(8, 9);
      return formatted;
    }, []);

    // Helper to toggle password visibility for a specific credential row
    const toggleCredentialPassword = (index) => {
        setShowCredentialPasswords(prev => {
            const updated = [...prev];
            updated[index] = !updated[index];
            return updated;
        });
    };

    useEffect(() => { 
        loadCountries(); 
        loadBanks();
        updateCities('TZ', true); // Pre-load Tanzanian cities
    }, [loadCountries, loadBanks, updateCities]);

    useEffect(() => {
        if (customer.country) {
            updateCities(customer.country); // Call updateCities when supplier.country changes
        } else {
                setCities([]); // Clear cities if country is cleared
            }
        }, [customer.country, updateCities]);

    // --- Form Field Handlers ---
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (readOnly) return;

        // Handle VRN N/A checkbox
        if (name === 'vrn_na') {
            setCustomer(prev => ({
                ...prev,
                vrn_na: checked,
                vrn: checked ? '' : prev.vrn // Clear VRN if N/A checked
            }));
            return;
        }

         // Skip address handling here since we have a custom handler
        if (name === 'address') return;

        let finalValue = value;
        if (type === 'text' || type === 'textarea') {
            finalValue = value.toUpperCase();
        }

        if (type === 'checkbox') {
            setCustomer(prev => ({ ...prev, [name]: checked }));
        } else if (name === 'phone') {
            setCustomer(prev => ({ ...prev, phone: formatPhoneInput(value) }));
        } else if (name === 'tin') {
            setCustomer(prev => ({ ...prev, tin: formatTIN(value) }));
        } else if (name === 'vrn') {
            setCustomer(prev => ({ ...prev, vrn: formatVRN(value) }));
        } else {
            setCustomer(prev => ({ ...prev, [name]: finalValue }));
        }
    };
    
    const handleRepeatedChange = (e, index, fieldName, subArrayName) => {
        if (readOnly) return;
        const { value, type } = e.target;

        let finalValue = value;
        if (type === 'text' && fieldName !== 'emails' && fieldName !== 'password' && fieldName !== 'username') {
            finalValue = value.toUpperCase();
        } else if (fieldName === 'username') {
            finalValue = value; // Do NOT uppercase username
        }
        if (type === 'email' || fieldName === 'emails') {
            finalValue = value.toLowerCase();
        }

        if (subArrayName === 'bank_details' && fieldName === 'bank') {
            const selectedBank = banks.find(b => b.id === parseInt(value));
            setCustomer(prev => {
                const updatedArray = [...prev.bank_details];
                updatedArray[index] = { 
                    ...updatedArray[index], 
                    bank: selectedBank ? selectedBank.id : '',
                    swift_code: selectedBank ? selectedBank.swift_code : ''
                };
                return { ...prev, bank_details: updatedArray };
            });
            return;
        }
        
        setCustomer(prev => {
            const updatedArray = [...prev[subArrayName]];
            updatedArray[index] = { ...updatedArray[index], [fieldName]: value };
            return { ...prev, [subArrayName]: updatedArray };
        });
    };

    const getDisplayAddress = (address) => {
        // Remove P.O.BOX prefix for display in the input field
        if (!address) return '';
        return address.replace(/^P\.O\.BOX\s*/i, '').trim();
    };

    const getFullAddress = (addressValue) => {
        // Add P.O.BOX prefix when saving
        if (!addressValue) return 'P.O.BOX ';
        const cleanAddress = addressValue.replace(/^P\.O\.BOX\s*/i, '').trim();
        return cleanAddress ? `P.O.BOX ${cleanAddress}` : 'P.O.BOX ';
    };

    const handleAddressChange = (e) => {
        if (readOnly) return;
        const inputValue = e.target.value;
        // Store with P.O.BOX prefix
        const fullAddress = getFullAddress(inputValue);
        setCustomer(prev => ({ ...prev, address: fullAddress }));
    };

    const handleEmailGroupChange = (value, validationState, index) => {
        setCustomer(prev => {
            const updatedArray = [...prev.email_groups];
            updatedArray[index] = { ...updatedArray[index], emails: value };
            return { ...prev, email_groups: updatedArray };
        });

        // Update validation errors
        setEmailValidationErrors(prev => ({
            ...prev,
            [`email_groups_${index}_emails`]: validationState.errors
        }));
    };

    const handleFileChange = (e, fieldName, index = null) => {
        if (readOnly) return;
        const file = e.target.files[0];
        if (!file) return;

        if (fieldName === 'tin_document' || fieldName === 'vrn_document' || fieldName === 'letterhead') {
            // Create preview URL for new files
            const previewURL = URL.createObjectURL(file);
            setCustomer(prev => ({ 
                ...prev, 
                [fieldName]: file,
                [`${fieldName}_preview`]: previewURL  // Store preview URL for new files
            }));
        } else if (fieldName === 'attachments' && index !== null) {
            setCustomer(prev => {
                const updatedAttachments = [...prev.attachments];
                updatedAttachments[index] = { 
                    ...updatedAttachments[index], 
                    file_obj: file, 
                    document_file: URL.createObjectURL(file) 
                };
                return { ...prev, attachments: updatedAttachments };
            });
        }
    };

    // --- Repeatable Section Logic ---
    const addRepeatedItem = (subArrayName, newItem) => {
        if (readOnly) return;
        if (subArrayName === 'bank_details') {
            const newBankItem = { bank: '', swift_code: '', currency: 'TZS', account_number: '' };
            setCustomer(prev => ({ ...prev, bank_details: [...prev.bank_details, newBankItem] }));
        } else {
            setCustomer(prev => ({ ...prev, [subArrayName]: [...prev[subArrayName], newItem] }));
        }
    };

    const removeRepeatedItem = (subArrayName, index) => {
        if (readOnly) return;
        setCustomer(prev => ({ ...prev, [subArrayName]: prev[subArrayName].filter((_, i) => i !== index) }));
    };

    // --- File Preview Modal Logic ---
    // Replace the openPreview function around line 285
    /* const openPreview = (fileURL, fileName) => {
        if (!previewModalRef.current || !previewIframeRef.current || !previewImageRef.current || !previewDownloadDivRef.current) return;

        const modal = previewModalRef.current;
        const iframe = previewIframeRef.current;
        const image = previewImageRef.current;
        const downloadDiv = previewDownloadDivRef.current;

        // Reset modal to default state
        setIsMaximized(false);
        modal.style.width = '80vw';
        modal.style.height = '80vh';
        modal.style.left = '50%';
        modal.style.top = '50%';
        modal.style.transform = 'translate(-50%, -50%)';

        // Improved file type detection
        let ext = '';
        
        // First try to get extension from the file URL
        if (fileURL && typeof fileURL === 'string') {
            // Extract filename from URL (handle both blob URLs and regular URLs)
            const urlParts = fileURL.split('/');
            const lastPart = urlParts[urlParts.length - 1];
            const fileNameFromUrl = lastPart.split('?')[0]; // Remove query parameters
            
            if (fileNameFromUrl.includes('.')) {
                ext = fileNameFromUrl.split('.').pop().toLowerCase();
            }
        }
        
        // Fallback: try to get extension from fileName parameter
        if (!ext && fileName && typeof fileName === 'string' && fileName.includes('.')) {
            ext = fileName.split('.').pop().toLowerCase();
        }
        
        // If still no extension, try to detect from URL patterns
        if (!ext && fileURL) {
            // Check if URL contains common file extensions
            const urlLower = fileURL.toLowerCase();
            if (urlLower.includes('.pdf')) ext = 'pdf';
            else if (urlLower.includes('.jpg') || urlLower.includes('.jpeg')) ext = 'jpg';
            else if (urlLower.includes('.png')) ext = 'png';
            else if (urlLower.includes('.gif')) ext = 'gif';
            else if (urlLower.includes('.doc')) ext = 'doc';
            else if (urlLower.includes('.docx')) ext = 'docx';
        }

        console.log('File preview debug:', { fileURL, fileName, detectedExt: ext });

        const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
        const pdfTypes = ['pdf'];
        const documentTypes = ['doc', 'docx', 'txt', 'rtf'];

        // Hide all preview elements
        image.style.display = 'none';
        iframe.style.display = 'none';
        downloadDiv.style.display = 'none';

        // Show appropriate preview
        if (imageTypes.includes(ext)) {
            console.log('Showing as image');
            image.src = fileURL;
            image.style.display = 'block';
            image.onload = () => console.log('Image loaded successfully');
            image.onerror = () => {
                console.log('Image failed to load, showing download instead');
                image.style.display = 'none';
                showDownloadOption();
            };
        } else if (pdfTypes.includes(ext)) {
            console.log('Showing as PDF');
            iframe.src = fileURL;
            iframe.style.display = 'block';
            iframe.onload = () => console.log('PDF loaded successfully');
            iframe.onerror = () => {
                console.log('PDF failed to load, showing download instead');
                iframe.style.display = 'none';
                showDownloadOption();
            };
        } else {
            console.log('Showing download option for file type:', ext);
            showDownloadOption();
        }

        function showDownloadOption() {
            const displayFileName = fileName || 'Document';
            const displayExt = ext ? `.${ext}` : '';
            
            downloadDiv.innerHTML = `
                <p style="font-size: 16px; color: #666; margin-bottom: 20px;">
                    Preview not available for this file type${displayExt ? ` (${displayExt})` : ''}
                </p>
                <a href="${fileURL}" download="${displayFileName}" class="download-link-btn">
                    Download ${displayFileName}
                </a>
            `;
            downloadDiv.style.display = 'block';
        }

        // Show modal
        modal.style.display = 'block';
    };*/

    // Replace the openPreview function
   /*  const openPreview = (fileURL, fileName) => {
        // Improved file type detection
        let ext = '';
        
        // First try to get extension from the file URL
        if (fileURL && typeof fileURL === 'string') {
            const urlParts = fileURL.split('/');
            const lastPart = urlParts[urlParts.length - 1];
            const fileNameFromUrl = lastPart.split('?')[0];
            
            if (fileNameFromUrl.includes('.')) {
                ext = fileNameFromUrl.split('.').pop().toLowerCase();
            }
        }
        
        // Fallback: try to get extension from fileName parameter
        if (!ext && fileName && typeof fileName === 'string' && fileName.includes('.')) {
            ext = fileName.split('.').pop().toLowerCase();
        }
        
        // If still no extension, try to detect from URL patterns
        if (!ext && fileURL) {
            const urlLower = fileURL.toLowerCase();
            if (urlLower.includes('.pdf')) ext = 'pdf';
            else if (urlLower.includes('.jpg') || urlLower.includes('.jpeg')) ext = 'jpg';
            else if (urlLower.includes('.png')) ext = 'png';
            else if (urlLower.includes('.gif')) ext = 'gif';
            else if (urlLower.includes('.doc')) ext = 'doc';
            else if (urlLower.includes('.docx')) ext = 'docx';
        }

        console.log('File preview debug:', { fileURL, fileName, detectedExt: ext });

        const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
        const pdfTypes = ['pdf'];

        // Calculate popup window size and position
        const screenWidth = window.screen.availWidth;
        const screenHeight = window.screen.availHeight;
        const windowWidth = Math.min(1200, screenWidth * 0.85);
        const windowHeight = Math.min(800, screenHeight * 0.85);
        const left = (screenWidth - windowWidth) / 2;
        const top = Math.max(0, (screenHeight - windowHeight) / 2 - 50); // Move up to account for browser chrome

        // Enhanced window features - this is the maximum we can hide in modern browsers
        const windowFeatures = [
            `width=${windowWidth}`,
            `height=${windowHeight}`,
            `left=${left}`,
            `top=${top}`,
            'resizable=yes',
            'scrollbars=no',
            'status=no',
            'menubar=no',
            'toolbar=no',
            'location=no',
            'directories=no',
            'personalbar=no',
            'copyhistory=no',
            'dependent=yes' // Makes it a dependent window
        ].join(',');

        // Open new popup window
        const previewWindow = window.open('', `preview_${Date.now()}`, windowFeatures);
        
        if (!previewWindow) {
            alert('Popup blocked! Please allow popups for this site to view documents.');
            return;
        }

        // Create the HTML content for the preview window
        const displayFileName = fileName || 'Document';
        const displayExt = ext ? `.${ext}` : '';

        // Enhanced styles to make it more modal-like and compensate for browser chrome
        const commonStyles = `
            <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { 
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
                    background: #ffffff;
                    display: flex;
                    flex-direction: column;
                    height: 100vh;
                    overflow: hidden;
                    
                    margin: 0 !important;
                    padding: 0 !important;
                }
                
                
                .custom-header {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    padding: 8px 16px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    min-height: 45px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.15);
                    user-select: none;
                    cursor: move;
                    
                    position: relative;
                    z-index: 1000;
                }
                
                .window-controls {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                
                .window-title { 
                    font-size: 14px; 
                    font-weight: 600; 
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex: 1;
                }
                
                .file-icon {
                    width: 20px;
                    height: 20px;
                    background: rgba(255,255,255,0.25);
                    border-radius: 3px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 10px;
                    font-weight: bold;
                    letter-spacing: -0.5px;
                }
                
                .window-controls { 
                    display: flex; 
                    gap: 6px; 
                    align-items: center;
                }
                
                .window-btn {
                    width: 30px;
                    height: 30px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.15s ease;
                    background: rgba(255,255,255,0.1);
                    color: white;
                    position: relative;
                    font-size: 0; 
                }
                
                .window-btn:hover {
                    background: rgba(255,255,255,0.2);
                    transform: scale(1.05);
                }
                
                .window-btn:active {
                    transform: scale(0.95);
                }
                
                
                .btn-download { background: rgba(40,167,69,0.9); }
                .btn-download:hover { background: rgba(40,167,69,1); }
                
                .btn-restore { background: rgba(108,117,125,0.9); }
                .btn-restore:hover { background: rgba(108,117,125,1); }
                
                .btn-maximize { background: rgba(0,123,255,0.9); }
                .btn-maximize:hover { background: rgba(0,123,255,1); }
                
                .btn-close { background: rgba(220,53,69,0.9); }
                .btn-close:hover { background: rgba(220,53,69,1); }
                
                
                .window-btn::after {
                    content: attr(data-tooltip);
                    position: absolute;
                    top: 35px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: rgba(0,0,0,0.9);
                    color: white;
                    padding: 4px 8px;
                    border-radius: 3px;
                    font-size: 11px;
                    white-space: nowrap;
                    opacity: 0;
                    pointer-events: none;
                    transition: opacity 0.2s ease;
                    z-index: 1001;
                }
                .window-btn:hover::after {
                    opacity: 1;
                }
                
                
                .content {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;
                    overflow: hidden;
                    background: #f8f9fa;
                    position: relative;
                }
                
                .image {
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.1);
                    border-radius: 4px;
                }
                
                .pdf-frame {
                    width: 100%;
                    height: 100%;
                    border: none;
                    background: white;
                }
                
                .download-area {
                    text-align: center;
                    padding: 40px;
                    background: white;
                    border-radius: 8px;
                    margin: 20px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.1);
                    max-width: 400px;
                }
                
                .download-area h2 {
                    color: #495057;
                    margin-bottom: 10px;
                    font-size: 20px;
                }
                
                .download-area p {
                    color: #6c757d;
                    margin-bottom: 25px;
                    font-size: 14px;
                }
                
                .download-btn-large {
                    padding: 12px 24px;
                    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
                    color: white;
                    text-decoration: none;
                    border-radius: 6px;
                    font-size: 14px;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 15px rgba(40,167,69,0.3);
                }
                
                .download-btn-large:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(40,167,69,0.4);
                    color: white;
                    text-decoration: none;
                }
                
                
                .loading, .error {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    color: #6c757d;
                }
            </style>
        `;

        let htmlContent = '';

        if (imageTypes.includes(ext)) {
            htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Preview: ${displayFileName}</title>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <link rel="icon" href="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><text y='20' font-size='20'>🖼️</text></svg>">
                    ${commonStyles}
                </head>
                <body>
                    <div class="custom-header" onmousedown="startDrag(event)">
                        <div class="window-title">
                            <div class="file-icon">IMG</div>
                            <span>Preview: ${displayFileName}</span>
                        </div>
                        <div class="window-controls">
                            <button class="window-btn btn-download" onclick="downloadFile()" data-tooltip="Download">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                                </svg>
                            </button>
                            <button class="window-btn btn-restore" onclick="restoreWindow()" data-tooltip="Restore" style="display:none;" id="restoreBtn">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M4 8h4V4h8v4h4v12H4V8zm6-2v2h4V6h-4z M6 10v8h8v-2H8v-6H6z"/>
                                </svg>
                            </button>
                            <button class="window-btn btn-maximize" onclick="maximizeWindow()" data-tooltip="Maximize" id="maximizeBtn">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                                </svg>
                            </button>
                            <button class="window-btn btn-close" onclick="window.close()" data-tooltip="Close">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div class="content">
                        <img src="${fileURL}" alt="Preview" class="image" 
                            onerror="showError()" onload="hideLoading()" />
                        <div class="loading" id="loading">Loading image...</div>
                    </div>
                    <script>
                        let isMaximized = false;
                        let originalState = {
                            width: window.outerWidth,
                            height: window.outerHeight,
                            left: window.screenX,
                            top: window.screenY
                        };
                        
                        function hideLoading() {
                            const loading = document.getElementById('loading');
                            if (loading) loading.style.display = 'none';
                        }
                        
                        function downloadFile() {
                            const a = document.createElement('a');
                            a.href = '${fileURL}';
                            a.download = '${displayFileName}';
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                        }
                        
                        function maximizeWindow() {
                            originalState = {
                                width: window.outerWidth,
                                height: window.outerHeight,
                                left: window.screenX,
                                top: window.screenY
                            };
                            window.resizeTo(screen.availWidth, screen.availHeight);
                            window.moveTo(0, 0);
                            document.getElementById('maximizeBtn').style.display = 'none';
                            document.getElementById('restoreBtn').style.display = 'flex';
                            isMaximized = true;
                        }
                        
                        function restoreWindow() {
                            window.resizeTo(originalState.width, originalState.height);
                            window.moveTo(originalState.left, originalState.top);
                            document.getElementById('maximizeBtn').style.display = 'flex';
                            document.getElementById('restoreBtn').style.display = 'none';
                            isMaximized = false;
                        }
                        
                        function showError() {
                            hideLoading();
                            document.querySelector('.content').innerHTML = '<div class="error"><h2>Error Loading Image</h2><p>Unable to display this image file.</p></div>';
                        }
                        
                        // Drag functionality
                        let isDragging = false;
                        let dragOffset = { x: 0, y: 0 };
                        
                        function startDrag(e) {
                            if (isMaximized) return;
                            isDragging = true;
                            dragOffset.x = e.clientX - window.screenX;
                            dragOffset.y = e.clientY - window.screenY;
                            document.addEventListener('mousemove', drag);
                            document.addEventListener('mouseup', stopDrag);
                            e.preventDefault();
                        }
                        
                        function drag(e) {
                            if (!isDragging) return;
                            window.moveTo(e.screenX - dragOffset.x, e.screenY - dragOffset.y);
                        }
                        
                        function stopDrag() {
                            isDragging = false;
                            document.removeEventListener('mousemove', drag);
                            document.removeEventListener('mouseup', stopDrag);
                        }
                        
                        // Focus the window;
                        window.focus();
                        
                        // Optional: Auto-close when parent window closes
                        window.addEventListener('beforeunload', function() {
                            if (window.opener && !window.opener.closed) {
                                // Parent is still open
                            }
                        });
                    </script>
                </body>
                </html>
            `;
        } else if (pdfTypes.includes(ext)) {
            htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Preview: ${displayFileName}</title>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <link rel="icon" href="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><text y='20' font-size='20'>📄</text></svg>">
                    ${commonStyles}
                </head>
                <body>
                    <div class="custom-header" onmousedown="startDrag(event)">
                        <div class="window-title">
                            <div class="file-icon">PDF</div>
                            <span>Preview: ${displayFileName}</span>
                        </div>
                        <div class="window-controls">
                            <button class="window-btn btn-download" onclick="downloadFile()" data-tooltip="Download">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                                </svg>
                            </button>
                            <button class="window-btn btn-restore" onclick="restoreWindow()" data-tooltip="Restore" style="display:none;" id="restoreBtn">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M4 8h4V4h8v4h4v12H4V8zm6-2v2h4V6h-4z"/>
                                </svg>
                            </button>
                            <button class="window-btn btn-maximize" onclick="maximizeWindow()" data-tooltip="Maximize" id="maximizeBtn">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                                </svg>
                            </button>
                            <button class="window-btn btn-close" onclick="window.close()" data-tooltip="Close">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div class="content">
                        <iframe src="${fileURL}" class="pdf-frame" 
                                onerror="showError()" onload="hideLoading()"></iframe>
                        <div class="loading" id="loading">Loading PDF...</div>
                    </div>
                    <script>
                        let isMaximized = false;
                        let originalState = {
                            width: window.outerWidth,
                            height: window.outerHeight,
                            left: window.screenX,
                            top: window.screenY
                        };
                        
                        function hideLoading() {
                            const loading = document.getElementById('loading');
                            if (loading) loading.style.display = 'none';
                        }
                        
                        function downloadFile() {
                            const a = document.createElement('a');
                            a.href = '${fileURL}';
                            a.download = '${displayFileName}';
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                        }
                        
                        function maximizeWindow() {
                            originalState = {
                                width: window.outerWidth,
                                height: window.outerHeight,
                                left: window.screenX,
                                top: window.screenY
                            };
                            window.resizeTo(screen.availWidth, screen.availHeight);
                            window.moveTo(0, 0);
                            document.getElementById('maximizeBtn').style.display = 'none';
                            document.getElementById('restoreBtn').style.display = 'flex';
                            isMaximized = true;
                        }
                        
                        function restoreWindow() {
                            window.resizeTo(originalState.width, originalState.height);
                            window.moveTo(originalState.left, originalState.top);
                            document.getElementById('maximizeBtn').style.display = 'flex';
                            document.getElementById('restoreBtn').style.display = 'none';
                            isMaximized = false;
                        }
                        
                        function showError() {
                            hideLoading();
                            document.querySelector('.content').innerHTML = '<div class="error"><h2>Error Loading PDF</h2><p>Unable to display this PDF file.</p></div>';
                        }
                        
                        // Drag functionality  
                        let isDragging = false;
                        let dragOffset = { x: 0, y: 0 };
                        
                        function startDrag(e) {
                            if (isMaximized) return;
                            isDragging = true;
                            dragOffset.x = e.clientX - window.screenX;
                            dragOffset.y = e.clientY - window.screenY;
                            document.addEventListener('mousemove', drag);
                            document.addEventListener('mouseup', stopDrag);
                            e.preventDefault();
                        }
                        
                        function drag(e) {
                            if (!isDragging) return;
                            window.moveTo(e.screenX - dragOffset.x, e.screenY - dragOffset.y);
                        }
                        
                        function stopDrag() {
                            isDragging = false;
                            document.removeEventListener('mousemove', drag);
                            document.removeEventListener('mouseup', stopDrag);
                        }
                        
                        // Focus the window
                        window.focus();
                        
                        // Hide loading after a delay as fallback
                        setTimeout(hideLoading, 3000);
                    </script>
                </body>
                </html>
            `;
        } else {
            // For unsupported file types
            htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Download: ${displayFileName}</title>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <link rel="icon" href="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><text y='20' font-size='20'>📎</text></svg>">
                    ${commonStyles}
                </head>
                <body>
                    <div class="custom-header" onmousedown="startDrag(event)">
                        <div class="window-title">
                            <div class="file-icon">${ext.toUpperCase().substring(0,3)}</div>
                            <span>Download: ${displayFileName}</span>
                        </div>
                        <div class="window-controls">
                            <button class="window-btn btn-download" onclick="downloadFile()" data-tooltip="Download">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                                </svg>
                            </button>
                            <button class="window-btn btn-close" onclick="window.close()" data-tooltip="Close">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div class="content">
                        <div class="download-area">
                            <h2>Preview not available</h2>
                            <p>Preview is not available for this file type${displayExt ? ` (${displayExt})` : ''}</p>
                            <a href="${fileURL}" download="${displayFileName}" class="download-btn-large" onclick="downloadFile()">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                                </svg>
                                Download ${displayFileName}
                            </a>
                        </div>
                    </div>
                    <script>
                        function downloadFile() {
                            const a = document.createElement('a');
                            a.href = '${fileURL}';
                            a.download = '${displayFileName}';
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                        }
                        
                        // Drag functionality
                        let isDragging = false;
                        let dragOffset = { x: 0, y: 0 };
                        
                        function startDrag(e) {
                            isDragging = true;
                            dragOffset.x = e.clientX - window.screenX;
                            dragOffset.y = e.clientY - window.screenY;
                            document.addEventListener('mousemove', drag);
                            document.addEventListener('mouseup', stopDrag);
                            e.preventDefault();
                        }
                        
                        function drag(e) {
                            if (!isDragging) return;
                            window.moveTo(e.screenX - dragOffset.x, e.screenY - dragOffset.y);
                        }
                        
                        function stopDrag() {
                            isDragging = false;
                            document.removeEventListener('mousemove', drag);
                            document.removeEventListener('mouseup', stopDrag);
                        }
                        
                        // Focus the window
                        window.focus();
                    </script>
                </body>
                </html>
            `;
        }

        // Write the HTML content to the popup window
        previewWindow.document.write(htmlContent);
        previewWindow.document.close();

        // Focus the popup window and bring it to front
        previewWindow.focus();
        
        // Try to bring the window to the front (browser dependent)
        if (previewWindow.moveToFront) {
            previewWindow.moveToFront();
        }
    }; */

    const openPreview = (fileURL, fileName) => {
        openFilePreview(fileURL, fileName, {
            customTitle: `Preview: ${fileName || 'Document'}`,
            onDownload: (data) => {
                // Custom download handler if needed
                console.log('Download requested:', data);
            },
            onClose: () => {
                // Custom close handler if needed
                console.log('Preview window closed');
            }
        });
    };
    // Enhanced closePreview function
    const closePreview = () => {
        if (previewModalRef.current) {
            previewModalRef.current.style.display = 'none';
            
            // Clean up iframe src to stop any loading
            if (previewIframeRef.current) {
                previewIframeRef.current.src = '';
            }
            
            // Clean up image src
            if (previewImageRef.current) {
                previewImageRef.current.src = '';
            }
            
            setIsMaximized(false);
        }
    };

    // Modal drag functionality
    const handleModalDragStart = (e) => {
        if (!previewModalRef.current) return;
        
        const modal = previewModalRef.current;
        const startX = e.clientX;
        const startY = e.clientY;
        const startLeft = parseInt(window.getComputedStyle(modal).left);
        const startTop = parseInt(window.getComputedStyle(modal).top);

        const handleMouseMove = (e) => {
            const newLeft = startLeft + e.clientX - startX;
            const newTop = startTop + e.clientY - startY;
            
            modal.style.left = `${newLeft}px`;
            modal.style.top = `${newTop}px`;
            modal.style.transform = 'none'; // Remove centering transform when dragging
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        e.preventDefault();
    };

    // Modal maximize/restore functionality
    const toggleMaximize = () => {
        if (!previewModalRef.current) return;
        
        const modal = previewModalRef.current;
        
        if (isMaximized) {
            // Restore to previous size
            modal.style.width = '80vw';
            modal.style.height = '80vh';
            modal.style.left = '50%';
            modal.style.top = '50%';
            modal.style.transform = 'translate(-50%, -50%)';
            setIsMaximized(false);
        } else {
            // Maximize
            modal.style.width = '100vw';
            modal.style.height = '100vh';
            modal.style.left = '0';
            modal.style.top = '0';
            modal.style.transform = 'none';
            setIsMaximized(true);
        }
    };
    
    // --- Notes Logic ---
    const addNote = () => {
        if (readOnly) return;
        const currentUserId = localStorage.getItem('kk_user_id');
        setNotes([...notes, {
            subject: '',
            notes: '',
            note_date: new Date().toISOString().split('T')[0],
            created_by_user: currentUserId
        }]);
    };

    const saveNotes = async (customerId) => {
        try {
            const payload = {
                customer_id: customerId,
                notes: notes.map(n => ({
                    id: n.id,
                    subject: (n.subject || '').toUpperCase(),
                    notes: (n.notes || '').toUpperCase(),
                    note_date: n.note_date,
                    created_by_user: n.created_by_user
                }))
            };
            const response = await fetch('/api/masters/customer-notes/bulk_create/', {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload)
            });
            return response.ok;
        } catch (err) {
            console.error('Error saving customer notes:', err);
            return false;
        }
    };

    // --- Form Submission ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (readOnly) return;
        setError(null);

        const formData = new FormData();

        // VRN validation: allow empty if vrn_na is true
        if (!customer.vrn_na && (!customer.vrn || customer.vrn.trim() === '')) {
            setError('VRN is required unless marked as N/A.');
            toast.error('VRN is required unless marked as N/A.');
            return;
        }

        // Validate all email groups
        const emailFields = customer.email_groups.map((group, index) => ({
            fieldPath: `email_groups.${index}.emails`,
            required: false, // Set to true if you want to make emails required
            label: `Email Group ${index + 1}`
        }));

        const emailValidation = validateFormEmails(customer, emailFields);
        
        if (!emailValidation.isValid) {
            setEmailValidationErrors(emailValidation.errors);
            setError('Please fix email validation errors before submitting.');
            toast.error('Please fix email validation errors before submitting.');
            return;
        }

        // Clear email validation errors if all are valid
        setEmailValidationErrors({});
        
        // Append simple fields, converting to uppercase where needed
        Object.entries(customer).forEach(([key, value]) => {
            if (['contact_persons', 'email_groups', 'credentials', 'tariffs', 'attachments', 'tin_document', 'vrn_document', 'letterhead'].includes(key)) {
                return; // Skip complex/file fields
            }
            if (key === 'country') {
                    const countryObject = countries.find(c => c.cca2 === customer[key]);
                    if (countryObject) {
                        formData.append(key, countryObject.name.toUpperCase()); // Send full country name
                    } else {
                        formData.append(key, value); // Fallback to CCA2 if name not found
                    }
            } else if (typeof value === 'boolean') {
                formData.append(key, value);
            } else if (value !== null && value !== undefined) {
                formData.append(key, typeof value === 'string' ? value.toUpperCase() : value);
            }
        });
        
        // Append file fields
        if (customer.tin_document instanceof File) formData.append('tin_document', customer.tin_document);
        if (customer.vrn_document instanceof File) formData.append('vrn_document', customer.vrn_document);
        if (customer.letterhead instanceof File) formData.append('letterhead', customer.letterhead);

        // Append nested arrays as JSON strings
        formData.append('contact_persons', JSON.stringify(customer.contact_persons));
        formData.append('email_groups', JSON.stringify(customer.email_groups));
        formData.append('credentials', JSON.stringify(customer.credentials));
        formData.append('tariffs', JSON.stringify(customer.tariffs));
        formData.append('bank_details', JSON.stringify(customer.bank_details));
        formData.append('destinations', JSON.stringify(customer.destinations));
        formData.append('vrn_na', customer.vrn_na);


        // Handle attachments separately to include files
        const attachmentsData = customer.attachments.map(att => ({
            id: att.id,
            attachment_type: att.attachment_type.toUpperCase(),
            // We can't send the file object in JSON, so we handle it next
        }));
        formData.append('attachments_json', JSON.stringify(attachmentsData));

        customer.attachments.forEach((attachment, index) => {
            if (attachment.file_obj instanceof File) {
                formData.append(`attachment_file_${index}`, attachment.file_obj);
            }
        });

        const url = initialData?.id ? `${API_BASE_URL}${initialData.id}/` : API_BASE_URL;
        const method = initialData?.id ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method: method,
                headers: getAuthHeaders(true),
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = Object.entries(errorData).map(([key, val]) => `${key}: ${val}`).join('; ');
                setError(`Save failed: ${errorMessage}`);
                toast.error(`Save failed: ${errorMessage}`);
                return;
            }

            const savedData = await response.json();
            const customerId = savedData.id || initialData?.id;
            if (notes.length > 0 && customerId) {
                await saveNotes(customerId);
            }

            toast.success('Customer saved successfully!');
            if (onClose) onClose(true);

        } catch (err) {
            setError('Failed to save customer. Network error or server unreachable.');
            toast.error('Failed to save customer. Network error or server unreachable.');
        }
    };

    const majorCurrencies = [  
        { id: 'EUR', text: 'EUR - Euro' },
        { id: 'GBP', text: 'GBP - British Pound Sterling' },
        { id: 'TZS', text: 'TZS - Tanzanian Shilling' },
        { id: 'USD', text: 'USD - United States Dollar' },
    ];

    
    return (
        <div className="customer-form-container">
            <h1>Customer Details</h1>
            <div className="toggle-buttons">
                <button onClick={() => document.querySelectorAll('.section-content').forEach(el => el.style.display = 'block')}>Expand All</button>
                <button onClick={() => document.querySelectorAll('.section-content').forEach(el => el.style.display = 'none')}>Collapse All</button>
            </div>

            {error && <div className="form-error">{error}</div>}

            <form onSubmit={handleSubmit}>
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Customer Information</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="row" style={{ gridTemplateColumns: '2fr 1fr 1.5fr', alignItems: 'flex-end' }}>
                            <div>
                                <label className="required">Name</label>
                                <input type="text" name="name" value={customer.name} onChange={handleChange} required readOnly={readOnly} />
                            </div>
                            <div>
                                <label>Short Name</label>
                                <input type="text" name="short_name" value={customer.short_name || ''} onChange={handleChange} readOnly={readOnly} />
                            </div>
                            <div>
                                <label className="required">Phone</label>
                                <input type="tel" name="phone" value={customer.phone} onChange={handleChange} required readOnly={readOnly} placeholder="+255-XXX-XXX-XXX" />
                            </div>
                        </div>
                        <div className="row">
                            <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '20px' }}>
                                <div>
                                    <label className="required">Address</label>
                                    <div className={`address-input-container ${readOnly ? 'readonly' : ''}`}>
                                        <span className="address-prefix">P.O.BOX</span>
                                        <input 
                                            type="text" 
                                            name="address" 
                                            value={getDisplayAddress(customer.address)}
                                            onChange={handleAddressChange}
                                            required 
                                            readOnly={readOnly}
                                            className="address-input"
                                            placeholder="Enter address details..."
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="required">Country</label>
                                    <Select2Dropdown
                                        options={countries.map(c => ({ id: c.cca2, text: c.name }))}
                                        value={customer.country}
                                        onChange={value => {
                                            setCustomer(prev => ({ ...prev, country: value }));
                                            updateCities(value);
                                        }}
                                        placeholder="SELECT COUNTRY"
                                        displayKey="text"
                                        valueKey="id"
                                        required
                                        disabled={readOnly}
                                        clearable={true}
                                    />
                                </div>
                                <div>
                                    <label className="required">City</label>
                                    <Select2Dropdown
                                        options={cities.map(c => ({ id: c.toUpperCase(), text: c.toUpperCase() }))}
                                        value={customer.city}
                                        onChange={value => setCustomer(prev => ({ ...prev, city: value }))}
                                        required
                                        disabled={readOnly}
                                        placeholder="SELECT CITY"
                                        displayKey="text"
                                        valueKey="id"
                                        clearable={true}
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <label htmlFor="client_reference_required">Client Reference Required</label>
                                <input type="checkbox" name="client_reference_required" id="client_reference_required" checked={customer.client_reference_required} onChange={handleChange} disabled={readOnly} />
                                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <label htmlFor="letterhead">Letterhead</label>
                                    <input type="file" ref={letterheadRef} id="letterhead" accept="image/*" onChange={(e) => handleFileChange(e, 'letterhead')} disabled={readOnly} />
                                    {customer.letterhead && (
                                        <span
                                            onClick={() => {
                                                const fileUrl = typeof customer.letterhead === 'string'
                                                    ? customer.letterhead
                                                    : URL.createObjectURL(customer.letterhead);
                                                let fileName = 'Letterhead';
                                                if (typeof customer.letterhead === 'string') {
                                                    const lastPart = customer.letterhead.split('/').pop();
                                                    if (lastPart && lastPart.includes('.')) {
                                                        fileName = lastPart.split('?')[0];
                                                    }
                                                } else if (customer.letterhead instanceof File) {
                                                    fileName = customer.letterhead.name;
                                                }
                                                openPreview(fileUrl, fileName);
                                            }}
                                            className="preview-link"
                                        >
                                            View Letterhead
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Registration Details */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Registration Details</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                            <div>
                                <label className="required">TIN</label>
                                <input type="text" name="tin" value={customer.tin} onChange={handleChange} required readOnly={readOnly} maxLength="11" placeholder="123-456-789" />
                            </div>
                            <div>
                                <label>TIN Document</label>
                                <input type="file" ref={tinDocumentRef} onChange={(e) => handleFileChange(e, 'tin_document')} disabled={readOnly} />
                                {/* Add preview link for existing TIN document */}
                                {customer.tin_document && (
                                    <div className="file-links" style={{ marginTop: '5px' }}>
                                        <span 
                                            onClick={() => {
                                                const fileUrl = typeof customer.tin_document === 'string' 
                                                    ? customer.tin_document 
                                                    : URL.createObjectURL(customer.tin_document);
                                                
                                                // Extract proper filename
                                                let fileName = 'TIN_Document';
                                                if (typeof customer.tin_document === 'string') {
                                                    // If it's a URL, try to extract filename
                                                    const urlParts = customer.tin_document.split('/');
                                                    const lastPart = urlParts[urlParts.length - 1];
                                                    if (lastPart && lastPart.includes('.')) {
                                                        fileName = lastPart.split('?')[0]; // Remove query parameters
                                                    }
                                                } else if (customer.tin_document instanceof File) {
                                                    fileName = customer.tin_document.name;
                                                }
                                                console.log('Debug - customer.tin_document:', customer.tin_document);
                                                console.log('Debug - typeof:', typeof customer.tin_document);
                                                if (typeof customer.tin_document === 'string') {
                                                    console.log('Debug - URL parts:', customer.tin_document.split('/'));
                                                }
                                                openPreview(fileUrl, fileName);
                                            }} 
                                            className="preview-link"
                                        >
                                            View TIN Document
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                            <div>
                                <label className="required">VRN</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <input
                                        type="text"
                                        name="vrn"
                                        value={customer.vrn}
                                        onChange={handleChange}
                                        required={!customer.vrn_na}
                                        readOnly={readOnly || customer.vrn_na}
                                    />
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'normal' }}>
                                        <input
                                            type="checkbox"
                                            name="vrn_na"
                                            checked={customer.vrn_na}
                                            onChange={handleChange}
                                            disabled={readOnly}
                                            style={{ marginLeft: '8px' }}
                                        />
                                        N/A
                                    </label>
                                </div>
                            </div>
                            <div>
                                <label>VRN Document</label>
                                <input type="file" ref={vrnDocumentRef} onChange={(e) => handleFileChange(e, 'vrn_document')} disabled={readOnly} />
                                {/* Add preview link for existing VRN document */}
                                {customer.vrn_document && (
                                    <div className="file-links" style={{ marginTop: '5px' }}>
                                        <span 
                                            onClick={() => {
                                                const fileUrl = typeof customer.vrn_document === 'string' 
                                                    ? customer.vrn_document 
                                                    : URL.createObjectURL(customer.vrn_document);
                                                
                                                // Extract proper filename
                                                let fileName = 'VRN_Document';
                                                if (typeof customer.vrn_document === 'string') {
                                                    // If it's a URL, try to extract filename
                                                    const urlParts = customer.vrn_document.split('/');
                                                    const lastPart = urlParts[urlParts.length - 1];
                                                    if (lastPart && lastPart.includes('.')) {
                                                        fileName = lastPart.split('?')[0]; // Remove query parameters
                                                    }
                                                } else if (customer.vrn_document instanceof File) {
                                                    fileName = customer.vrn_document.name;
                                                }
                                                
                                                openPreview(fileUrl, fileName);
                                            }} 
                                            className="preview-link"
                                        >
                                            View VRN Document
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Bank Details</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        {customer.bank_details.map((detail, index) => (
                            <div key={index} className="repeatable-row" style={{ gridTemplateColumns: '2fr 1fr 1fr 2fr auto', alignItems: 'flex-end' }}>
                                <div>
                                    <label>Bank Name</label>
                                    <Select2Dropdown 
                                        options={banks.map(b => ({ id: b.id, text: b.name }))} 
                                        value={detail.bank} 
                                        onChange={value => handleRepeatedChange({ target: { value } }, index, 'bank', 'bank_details')} 
                                        disabled={readOnly} 
                                        required 
                                        placeholder="SELECT BANK"
                                        valueKey="id"
                                        displayKey="text"
                                    />
                                </div>
                                <div>
                                    <label>Swift Code</label>
                                    <input type="text" value={detail.swift_code} readOnly placeholder="Auto-populated" />
                                </div>
                                <div>
                                    <label>Currency</label>
                                    <Select2Dropdown 
                                        options={majorCurrencies} 
                                        value={detail.currency} 
                                        onChange={value => handleRepeatedChange({ target: { value } }, index, 'currency', 'bank_details')} 
                                        disabled={readOnly} 
                                        required 
                                        placeholder="CURRENCY"
                                        valueKey="id"
                                        displayKey="text"
                                    />
                                </div>
                                <div>
                                    <label>Account Number</label>
                                    <input type="text" value={detail.account_number} onChange={(e) => handleRepeatedChange(e, index, 'account_number', 'bank_details')} readOnly={readOnly} maxLength="25" required />
                                </div>
                                {!readOnly && <button type="button" onClick={() => removeRepeatedItem('bank_details', index)} className="remove-btn">Remove</button>}
                            </div>
                        ))}
                        {!readOnly && <button type="button" onClick={() => addRepeatedItem('bank_details')}>Add Bank Detail</button>}
                    </div>
                </div>

                {/* Contact Person Details */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Contact Person Details</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        {customer.contact_persons.map((person, index) => (
                            <div key={index} className="repeatable-row">
                                <div><input type="text" placeholder="Contact Person Name" value={person.name} onChange={(e) => handleRepeatedChange(e, index, 'name', 'contact_persons')} readOnly={readOnly} /></div>
                                <div><input type="text" placeholder="Contact Phone" value={person.phone} onChange={(e) => handleRepeatedChange(e, index, 'phone', 'contact_persons')} readOnly={readOnly} /></div>
                                {!readOnly && <button type="button" onClick={() => removeRepeatedItem('contact_persons', index)} className="remove-btn">Remove</button>}
                            </div>
                        ))}
                        {!readOnly && <button type="button" onClick={() => addRepeatedItem('contact_persons', { name: '', phone: '' })}>Add Contact Person</button>}
                    </div>
                </div>

                {/* Customer Email Groups */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Customer Email Groups</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        {customer.email_groups.map((group, index) => (
                            <React.Fragment key={index}>
                            <div className="repeatable-row" style={index === 0 ? { marginBottom: 4, paddingBottom: 4 } : undefined}>
                                <div style={{ flex: '1' }}><label className="required">Email Group</label>
                                <input type="text" value={group.group_name || ''} onChange={(e) => handleRepeatedChange(e, index, 'group_name', 'email_groups')} required readOnly={readOnly} /></div>
                                <div style={{ flex: '2' }}><label className="required">Emails</label>
                                    <EmailInput
                                        value={group.emails}
                                        onChange={(value, validationState) => handleEmailGroupChange(value, validationState, index)}
                                        placeholder="Email addresses (separate with comma or semicolon)"
                                        readOnly={readOnly}
                                        required={false} // Set to true if you want to make emails required
                                        showValidationStatus={false}
                                        showHelpText={false}
                                    />
                                </div>
                                {!readOnly && <button type="button" onClick={() => removeRepeatedItem('email_groups', index)} className="remove-btn">Remove</button>}
                            </div>
                            {index === 0 && (
                                <div style={{ fontWeight: 'bold', margin: '0 0 12px', fontSize: 12, color: '#333' }}>
                                    NOTE: This group will be used for sending all autogenerated emails from Software (Customer Passwords, TMDA Applications, Client Reports, etc.). Ensure you have kept the correct email IDs.
                                </div>
                            )}
                            </React.Fragment>
                        ))}
                        {!readOnly && (
                            <button 
                                type="button" 
                                onClick={() => addRepeatedItem('email_groups', { group_name: '', emails: '' })}
                            >
                                Add Email Group
                            </button>
                        )}
                    </div>
                </div>
                
                {/* Authority Portal Credentials */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Authority Portal Credentials</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        {customer.credentials.map((cred, index) => (
                            <div key={index} className="repeatable-row credentials-row">
                                <div><input type="text" placeholder="Authority" value={cred.authority} onChange={(e) => handleRepeatedChange(e, index, 'authority', 'credentials')} readOnly={readOnly} /></div>
                                <div><input type="text" placeholder="Username" value={cred.username} onChange={(e) => handleRepeatedChange(e, index, 'username', 'credentials')} style={{ textTransform: 'none', paddingRight: 30 }} readOnly={readOnly} /></div>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={showCredentialPasswords[index] ? "text" : "password"}
                                        placeholder="Password"
                                        value={cred.password}
                                        onChange={(e) => handleRepeatedChange(e, index, 'password', 'credentials')}
                                        readOnly={readOnly}
                                        style={{ textTransform: 'none', paddingRight: 30 }}
                                        autoComplete="current-password"
                                    />
                                    <span
                                        className="eye-icon"
                                        onClick={() => toggleCredentialPassword(index)}
                                        style={{
                                            position: 'absolute',
                                            right: 8,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            cursor: 'pointer',
                                            color: '#666'
                                        }}
                                        aria-label={showCredentialPasswords[index] ? "Hide password" : "Show password"}
                                        tabIndex={0}
                                    >
                                        {showCredentialPasswords[index] ? (
                                            // Eye with slash (hide password)
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                                <line x1="1" y1="1" x2="23" y2="23"/>
                                            </svg>
                                        ) : (
                                            // Eye (show password)
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                                <circle cx="12" cy="12" r="3"/>
                                            </svg>
                                        )}
                                    </span>
                                </div>
                                <div><input type="text" placeholder="Other" value={cred.other} onChange={(e) => handleRepeatedChange(e, index, 'other', 'credentials')} readOnly={readOnly} /></div>
                                <div><input type="text" placeholder="Remarks" value={cred.remarks} onChange={(e) => handleRepeatedChange(e, index, 'remarks', 'credentials')} readOnly={readOnly} /></div>
                                {!readOnly && <button type="button" onClick={() => removeRepeatedItem('credentials', index)} className="remove-btn">Remove</button>}
                            </div>
                        ))}
                        {!readOnly && <button type="button" onClick={() => addRepeatedItem('credentials', { authority: '', username: '', password: '', other: '', remarks: '' })}>Add Credential</button>}
                    </div>
                </div>
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Destinations</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        {customer.destinations.map((dest, index) => (
                            <div key={index} className="repeatable-row" style={{ gridTemplateColumns: 'min-content auto', alignItems: 'flex-end' }}>
                                <div style={{ width: 'max-content', minWidth: '250px' }}>
                                    <label>City</label>
                                    <Select2Dropdown 
                                        options={tanzanianCities.map(c => ({ id: c.toUpperCase(), text: c.toUpperCase() }))} 
                                        value={dest.city} 
                                        onChange={value => handleRepeatedChange({ target: { value } }, index, 'city', 'destinations')} 
                                        disabled={readOnly} 
                                        required 
                                        placeholder="SELECT DESTINATION CITY"
                                        valueKey="id"
                                        displayKey="text"
                                    />
                                </div>
                                {!readOnly && <button type="button" onClick={() => removeRepeatedItem('destinations', index)} className="remove-btn">Remove</button>}
                            </div>
                        ))}
                        {!readOnly && <button type="button" onClick={() => addRepeatedItem('destinations', { city: '' })}>Add Destination</button>}
                    </div>
                </div>
                {/* Customer Tariffs */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Customer Tariffs</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                       {customer.tariffs.map((tariff, index) => (
                            <div key={index} className="repeatable-row">
                                <div><input type="text" placeholder="Tariff Name" value={tariff.tariff_name} onChange={(e) => handleRepeatedChange(e, index, 'tariff_name', 'tariffs')} readOnly={readOnly} /></div>
                                <div><input type="text" placeholder="Unit Type" value={tariff.unit_type} onChange={(e) => handleRepeatedChange(e, index, 'unit_type', 'tariffs')} readOnly={readOnly} /></div>
                                <div><input type="number" step="0.01" placeholder="Unit Rate" value={tariff.unit_rate} onChange={(e) => handleRepeatedChange(e, index, 'unit_rate', 'tariffs')} readOnly={readOnly} /></div>
                                {!readOnly && <button type="button" onClick={() => removeRepeatedItem('tariffs', index)} className="remove-btn">Remove</button>}
                            </div>
                        ))}
                        {!readOnly && <button type="button" onClick={() => addRepeatedItem('tariffs', { tariff_name: '', unit_type: '', unit_rate: '' })}>Add Tariff</button>}
                    </div>
                </div>

                {/* Attachments */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Attachments</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                     {customer.attachments.map((attachment, index) => (
                        <div key={index} className="repeatable-row">
                            <div><input type="text" placeholder="Attachment Type" value={attachment.attachment_type} onChange={(e) => handleRepeatedChange(e, index, 'attachment_type', 'attachments')} readOnly={readOnly} /></div>
                            <div><input type="file" onChange={(e) => handleFileChange(e, 'attachments', index)} disabled={readOnly} /></div>
                            {(attachment.document_file) && (
                                <div><span onClick={() => openPreview(typeof attachment.document_file === 'string' ? attachment.document_file : URL.createObjectURL(attachment.file_obj), attachment.file_obj?.name || 'file')} className="preview-link">
                                    View
                                </span></div>
                            )}
                            {!readOnly && <button type="button" onClick={() => removeRepeatedItem('attachments', index)} className="remove-btn">Remove</button>}
                        </div>
                    ))}
                        {!readOnly && <button type="button" onClick={() => addRepeatedItem('attachments', { attachment_type: '', document_file: null, file_obj: null })}>Add Attachment</button>}
                    </div>
                </div>

                {/* Notes Section */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Notes ({notes.length})</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        {notes.map((note, index) => (
                            <div key={index} className="repeatable-row">
                                <div>
                                    <label>Subject</label>
                                    <input
                                        type="text"
                                        placeholder="Enter Subject"
                                        value={note.subject || ''}
                                        onChange={(e) => {
                                            const updated = [...notes];
                                            updated[index].subject = e.target.value.toUpperCase();
                                            setNotes(updated);
                                        }}
                                        readOnly={readOnly}
                                        style={{ textTransform: 'uppercase' }}
                                    />
                                </div>
                                <div>
                                    <label>Notes</label>
                                    <textarea
                                        placeholder="Enter Notes"
                                        value={note.notes || ''}
                                        onChange={(e) => {
                                            const updated = [...notes];
                                            updated[index].notes = e.target.value.toUpperCase();
                                            setNotes(updated);
                                        }}
                                        readOnly={readOnly}
                                        rows={3}
                                        style={{ textTransform: 'uppercase' }}
                                    />
                                </div>                                
                                <div>
                                    <label>Date</label>
                                    <input
                                        type="date"
                                        value={note.note_date || ''}
                                        onChange={(e) => {
                                            const updated = [...notes];
                                            updated[index].note_date = e.target.value;
                                            setNotes(updated);
                                        }}
                                        readOnly={true}
                                    />
                                </div>
                                <div>
                                    <label>Created By</label>
                                    <input
                                        type="text"
                                        value={localStorage.getItem('kk_user') || 'Unknown User'}
                                        readOnly={true}
                                        style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                </div>
                                {!readOnly && (
                                    <button type="button" onClick={() => setNotes(notes.filter((_, i) => i !== index))} className="remove-btn">
                                        Remove
                                    </button>
                                )}
                            </div>
                        ))}
                        {!readOnly && (
                            <button type="button" onClick={addNote}>Add Note</button>
                        )}
                    </div>
                </div>

                {/* Remarks */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Remarks</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <textarea name="remarks" value={customer.remarks} onChange={handleChange} readOnly={readOnly} placeholder="Additional Remarks"></textarea>
                    </div>
                </div>

                <div className="buttons">
                    {/* 3. Conditionally render the "Edit" button when in view mode */}
                    {readOnly && hasPermission('CUSTOMERS', 'can_edit') && (
                        <button type="button" onClick={() => setReadOnly(false)}>Edit</button>
                    )}
                    {/* 4. Conditionally render the "Save" button when in an editable mode */}
                    {!readOnly && (
                        <button type="submit">Save</button>
                    )}
                    <button type="button" onClick={() => onClose(false)} className="cancel-btn">Close</button>
                </div>
            </form>

            {/* Updated Preview Modal */}
            <div 
                id="previewModal" 
                ref={previewModalRef} 
                style={{ 
                    display: 'none', 
                    position: 'fixed',
                    zIndex: '1000',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80vw',
                    height: '80vh',
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                    resize: 'both',
                    overflow: 'auto',
                    minWidth: '400px',
                    minHeight: '300px'
                }}
            >
                <div 
                    id="modalHeader" 
                    style={{
                        padding: '10px 15px',
                        backgroundColor: '#f8f9fa',
                        borderBottom: '1px solid #ddd',
                        borderRadius: '8px 8px 0 0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'move',
                        userSelect: 'none'
                    }}
                    onMouseDown={handleModalDragStart}
                >
                    <span style={{ fontWeight: 'bold', fontSize: '14px' }}>File Preview</span>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button 
                            onClick={toggleMaximize}
                            style={{
                                padding: '5px 10px',
                                border: 'none',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                            }}
                        >
                            {isMaximized ? 'Restore' : 'Maximize'}
                        </button>
                        <button 
                            onClick={closePreview}
                            style={{
                                padding: '5px 10px',
                                border: 'none',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
                <div style={{ padding: '15px', height: 'calc(100% - 60px)', overflow: 'auto' }}>
                    <iframe 
                        id="previewFrame" 
                        ref={previewIframeRef} 
                        title="File Preview"
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            display: 'none'
                        }}
                    ></iframe>
                    <img 
                        id="previewImage" 
                        ref={previewImageRef} 
                        alt="Preview"
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            display: 'none'
                        }}
                    />
                    <div 
                        id="downloadDiv" 
                        ref={previewDownloadDivRef}
                        style={{
                            textAlign: 'center',
                            padding: '50px',
                            display: 'none'
                        }}
                    ></div>
                </div>
            </div>
        </div>
    );
};

export default CustomerForm;
